import React from "react";
import Icon from "@/ui/Icon";
import Separator from "@/ui/Separator";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { MenuItemProps, MenuOption, UserMenuProps } from "./types";
import { USER_MENU_MAIN_OPTIONS } from "@/lib/constants";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/library/popover";
import ImageWithFallback from "@/ui/ImageWithFallback";
import usePathWithSlug from "@/hooks/usePathWithSlug";

const MenuItem: React.FC<MenuItemProps> = ({ icon, to, onClick, className, children, external }) => {
  const pathWithSlug = usePathWithSlug();
  const url = !external && !!to ? pathWithSlug(to) : to;
  const baseStyles =
    "flex items-center text-base font-normal leading-normal text-[#B8B8B8] transition-colors duration-200 hover:text-white mb-4";

  return url ? (
    <Link to={url} target={external ? "_blank" : undefined} className={cn(baseStyles, className)}>
      <Icon name={icon} className="mr-2" />
      {children}
    </Link>
  ) : (
    <button onClick={onClick} className={cn(baseStyles, "w-full text-left", className)}>
      <Icon name={icon} className="mr-2" />
      {children}
    </button>
  );
};

export const UserMenu: React.FC<UserMenuProps> = ({ logout, image }) => {
  const secondaryOptions: MenuOption[] = [
    {
      name: "Profile Settings",
      to: "/profile",
      icon: "gear",
      visible: true,
    },
    {
      name: "Migrate Account",
      to: "https://account-migration.thrive.xyz/",
      icon: "merge",
      visible: true,
      external: true,
    },
    {
      name: "Log out",
      icon: "sign-out",
      onClick: () => {
        sessionStorage.removeItem("onboarding_skipped");
        logout();
      },
      visible: true,
      className: "text-[#FF9592] hover:text-[#FF6B66] mb-0",
    },
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center justify-center overflow-hidden rounded-full bg-transparent p-0"
          aria-label="Account menu"
        >
          <ImageWithFallback src={image} alt="Profile" className="h-10 w-10 rounded-full object-cover" type="user" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={8}
        className="w-60 translate-x-1/2 transform border-none p-0 md:translate-x-0"
      >
        <div className="rounded-2xl bg-[#111318] p-6 shadow-lg ring-1 ring-black/10">
          {USER_MENU_MAIN_OPTIONS.filter((item) => item.visible).map(
            ({ name, to, icon, onClick, className, external }) => (
              <MenuItem key={name} icon={icon} external={external} to={to} onClick={onClick} className={className}>
                {name}
              </MenuItem>
            ),
          )}

          <Separator className="my-4" />

          {secondaryOptions
            .filter((item) => item.visible)
            .map(({ name, to, icon, onClick, className, external }) => (
              <MenuItem key={name} icon={icon} external={external} to={to} onClick={onClick} className={className}>
                {name}
              </MenuItem>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default UserMenu;
